/**
 * 管理端 / 院长看板（数据口径：全机构）
 *
 * 布局：① 6 张 KPI ② 待办 + 学生构成（学部）
 *      ③ 在籍学生结构（年级）+ 学院概览
 *      ④ 教学运行：作业 / 课题 / 考勤（作业与课题数据未录入时显示占位，结构已留好）
 *      ⑤ 最近操作日志 + 角色分布/报名批次/快捷入口
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card, Row, Col, Statistic, List, Tag, Spin, Typography, Empty, message,
  Button, Modal, Table, Divider, Space, Progress, Tooltip,
} from 'antd';
import {
  UserOutlined, BankOutlined, TeamOutlined, CalendarOutlined,
  BookOutlined, TrophyOutlined, PlusOutlined, RightOutlined,
} from '@ant-design/icons';
import ReactECharts from 'echarts-for-react';
import { authApi } from '../../../services/api';
import type { OperationLogItem, DashboardSummary } from '../../../services/types';
import { GRADE_MAP } from '../../../utils/courseConstants';

const { Text } = Typography;

/** 操作日志的模块显示名（日志里的 module 是英文 key） */
const MODULE_LABEL: Record<string, string> = {
  branches: '学院管理',
  teachers: '教师管理',
  students: '学生管理',
  courses: '课程管理',
  activities: '活动管理',
  competitions: '比赛管理',
  grades: '成绩管理',
  users: '用户管理',
  notices: '通知公告',
  auth: '登录认证',
};

const formatLogTime = (v?: string) =>
  v ? `${v.slice(0, 4)}-${v.slice(5, 7)}-${v.slice(8, 10)} ${v.slice(11, 16)}` : '-';

const roleDisplayMap: Record<string, string> = {
  admin: '系统管理员',
  teacher: '教师',
  dean: '院长',
  student: '学生',
  deputydean: '副院长',
  sci_assistant: '少科院助理',
};

type TodoItem = {
  key: string;
  label: string;
  count: number;
  to: string;
};

const OverviewDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [summary, setSummary] = useState<DashboardSummary | null>(null);
  const [summaryLoading, setSummaryLoading] = useState(true);

  // 操作日志弹窗（服务端分页，每页 20 条）
  const [logsOpen, setLogsOpen] = useState(false);
  const [logs, setLogs] = useState<OperationLogItem[]>([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logsPage, setLogsPage] = useState(1);
  const [logsTotal, setLogsTotal] = useState(0);

  useEffect(() => {
    authApi.getDashboardSummary()
      .then((res) => setSummary(res.data || null))
      .catch(() => message.error('加载看板数据失败'))
      .finally(() => setSummaryLoading(false));
  }, []);

  const fetchLogs = (page: number) => {
    setLogsLoading(true);
    authApi.getOperationLogs({ page, page_size: 20 }).then((res) => {
      const payload: any = res?.data || {};
      setLogs(payload.results || []);
      setLogsTotal(payload.count || 0);
    }).catch(() => message.error('加载操作日志失败'))
      .finally(() => setLogsLoading(false));
  };

  const openLogs = () => {
    setLogsOpen(true);
    setLogsPage(1);
    fetchLogs(1);
  };

  const logColumns = [
    { title: '操作时间', dataIndex: 'created_at', key: 'created_at', width: 150, render: (v: string) => formatLogTime(v) },
    { title: '操作人', dataIndex: 'operator', key: 'operator', width: 100, render: (v: string) => v || '系统' },
    {
      title: '模块', dataIndex: 'module', key: 'module', width: 110,
      render: (v: string) => <Tag>{MODULE_LABEL[v] || v || '-'}</Tag>,
    },
    {
      title: '操作内容', dataIndex: 'description', key: 'description', ellipsis: true,
      render: (v: string, row: OperationLogItem) => {
        const desc = row.operator && v?.startsWith(row.operator) ? v.slice(row.operator.length) : v;
        return <span title={v}>{desc || '-'}</span>;
      },
    },
    { title: 'IP', dataIndex: 'ip_address', key: 'ip_address', width: 130, render: (v: string) => v || '—' },
  ];

  if (summaryLoading) {
    return <div style={{ textAlign: 'center', padding: 60 }}><Spin size="large" /></div>;
  }
  if (!summary) {
    return <Empty description="看板数据加载失败，请刷新页面重试" />;
  }

  const {
    kpi, todos, distribution, teaching, role_distribution, open_enrollment_batches, recent_logs,
  } = summary;

  const todoItems: TodoItem[] = [
    { key: 'pending_enrollments', label: '待审核报名', count: todos.pending_enrollments, to: '/students' },
    { key: 'pending_homework', label: '待批作业', count: todos.pending_homework, to: '/courses' },
    { key: 'pending_research', label: '待审课题', count: todos.pending_research, to: '/courses' },
    { key: 'sessions_without_attendance', label: '未录考勤课次', count: todos.sessions_without_attendance, to: '/courses' },
    { key: 'draft_grade_batches', label: '待录成绩批次', count: todos.draft_grade_batches, to: '/grades' },
    { key: 'pending_grade_review', label: '待审成绩批次', count: todos.pending_grade_review, to: '/grades' },
  ];
  const todoTotal = todoItems.reduce((sum, item) => sum + item.count, 0);

  const kpiCards = [
    { title: '用户总数', value: kpi.total_users, icon: <UserOutlined />, color: '#16a34a' },
    { title: '学院数量', value: kpi.total_branches, icon: <BankOutlined />, color: '#52c41a' },
    { title: '在籍学生', value: kpi.enrolled_students, icon: <TeamOutlined />, color: '#722ed1' },
    { title: '今日活动', value: kpi.today_activities, icon: <CalendarOutlined />, color: '#fa8c16' },
    { title: '今日比赛', value: kpi.today_competitions, icon: <TrophyOutlined />, color: '#eb2f96' },
    { title: '本月新增学生', value: kpi.new_students_this_month, icon: <PlusOutlined />, color: '#1677ff' },
  ];

  const stageData = [
    { name: '小学部', value: distribution.by_stage.primary || 0 },
    { name: '初中部', value: distribution.by_stage.middle || 0 },
    { name: '高中部', value: distribution.by_stage.high || 0 },
  ].filter((item) => item.value > 0);

  const stageOption = {
    tooltip: { trigger: 'item', formatter: '{b}: {c} 人 ({d}%)' },
    legend: { bottom: 0, itemWidth: 10, itemHeight: 10, textStyle: { fontSize: 12 } },
    series: [
      {
        type: 'pie',
        radius: ['45%', '68%'],
        center: ['50%', '44%'],
        avoidLabelOverlap: true,
        label: { show: false },
        data: stageData,
      },
    ],
  };

  // 在籍学生按年级（柱子按学部着色：小学绿 / 初中蓝 / 高中紫）
  const STAGE_COLOR: Record<string, string> = { primary: '#52c41a', middle: '#1677ff', high: '#722ed1' };
  const stageOfGrade = (grade: string) => {
    const n = Number(grade);
    if (n >= 1 && n <= 6) return 'primary';
    if (n >= 7 && n <= 9) return 'middle';
    return 'high';
  };
  const gradeRows = distribution.by_grade || [];
  const gradeOption = {
    tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
    grid: { left: 8, right: 16, top: 24, bottom: 8, containLabel: true },
    xAxis: {
      type: 'category',
      data: gradeRows.map((row) => GRADE_MAP[row.grade] || row.grade),
      axisLine: { lineStyle: { color: '#e5e7eb' } },
      axisLabel: { color: '#8c8c8c', fontSize: 11 },
    },
    yAxis: {
      type: 'value',
      minInterval: 1,
      splitLine: { lineStyle: { color: '#f0f0f0' } },
      axisLabel: { color: '#8c8c8c', fontSize: 11 },
    },
    series: [
      {
        name: '在籍学生',
        type: 'bar',
        barMaxWidth: 26,
        data: gradeRows.map((row) => ({
          value: row.count,
          itemStyle: { color: STAGE_COLOR[stageOfGrade(row.grade)], borderRadius: [4, 4, 0, 0] },
        })),
        label: { show: true, position: 'top', fontSize: 11, color: '#8c8c8c' },
      },
    ],
  };

  // 学生状态（在籍 / 退学 …）
  const enrolledCount = kpi.enrolled_students;
  const withdrawnCount = (distribution.by_status || [])
    .filter((row) => row.status !== 'enrolled')
    .reduce((sum, row) => sum + row.count, 0);

  // 教学运行：作业 / 课题 / 考勤
  const hw = teaching.homework;
  const research = teaching.research;
  const att = teaching.attendance;
  const attendanceOption = {
    tooltip: { trigger: 'item', formatter: '{b}: {c} 人次 ({d}%)' },
    legend: { bottom: 0, itemWidth: 10, itemHeight: 10, textStyle: { fontSize: 12 } },
    series: [
      {
        type: 'pie',
        radius: ['45%', '68%'],
        center: ['50%', '44%'],
        label: { show: false },
        data: [
          { name: '出勤', value: att.present, itemStyle: { color: '#52c41a' } },
          { name: '迟到', value: att.late, itemStyle: { color: '#faad14' } },
          { name: '请假', value: att.leave, itemStyle: { color: '#1677ff' } },
          { name: '缺勤', value: att.absent, itemStyle: { color: '#ff4d4f' } },
        ].filter((item) => item.value > 0),
      },
    ],
  };

  const topBranches = distribution.by_branch.slice(0, 6);
  const branchOption = {
    tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
    grid: { left: 8, right: 36, top: 10, bottom: 8, containLabel: true },
    xAxis: { type: 'value', minInterval: 1, splitLine: { lineStyle: { color: '#f0f0f0' } }, axisLabel: { color: '#8c8c8c', fontSize: 11 } },
    yAxis: {
      type: 'category',
      inverse: true,
      data: topBranches.map((b) => b.name),
      axisLabel: { fontSize: 11, color: '#595959', width: 96, overflow: 'truncate' },
      axisLine: { lineStyle: { color: '#e5e7eb' } },
    },
    series: [
      {
        name: '在籍学生',
        type: 'bar',
        barWidth: 12,
        data: topBranches.map((b) => b.student_count),
        itemStyle: { color: '#1677ff', borderRadius: [0, 6, 6, 0] },
        label: { show: true, position: 'right', fontSize: 11, color: '#8c8c8c' },
      },
    ],
  };

  return (
    <>
      {/* ① KPI */}
      <Row gutter={[16, 16]}>
        {kpiCards.map((card) => (
          <Col key={card.title} xs={12} md={8} xl={4}>
            <Card style={{ height: '100%' }}>
              <Statistic title={card.title} value={card.value} prefix={card.icon} styles={{ content: { color: card.color, fontSize: 26 } }} />
            </Card>
          </Col>
        ))}
      </Row>

      {/* ② 待办 + 学生构成 */}
      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col xs={24} lg={14}>
          <Card
            title="待办事项"
            size="small"
            style={{ height: '100%' }}
            extra={todoTotal > 0
              ? <Tag color="red">{todoTotal} 项待处理</Tag>
              : <Tag color="green">全部处理完毕</Tag>}
          >
            <List
              size="small"
              dataSource={todoItems}
              renderItem={(item) => (
                <List.Item
                  style={{ padding: '8px 0', cursor: 'pointer' }}
                  onClick={() => navigate(item.to)}
                  extra={
                    <Space size={6}>
                      <Text strong style={{ fontSize: 16, color: item.count > 0 ? '#ff4d4f' : '#bfbfbf' }}>{item.count}</Text>
                      <RightOutlined style={{ fontSize: 10, color: '#bfbfbf' }} />
                    </Space>
                  }
                >
                  <Text>{item.label}</Text>
                </List.Item>
              )}
            />
          </Card>
        </Col>
        <Col xs={24} lg={10}>
          <Card title="学生构成（按学部）" size="small" style={{ height: '100%' }}>
            {stageData.length > 0
              ? <ReactECharts option={stageOption} style={{ height: 232 }} notMerge lazyUpdate />
              : <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无在籍学生" style={{ padding: '40px 0' }} />}
          </Card>
        </Col>
      </Row>

      {/* ③ 在籍学生结构（按年级） + 学院概览 */}
      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col xs={24} lg={14}>
          <Card
            title="在籍学生结构（按年级）"
            size="small"
            style={{ height: '100%' }}
            extra={<Text type="secondary" style={{ fontSize: 12 }}>在籍 {enrolledCount} · 其他状态 {withdrawnCount}</Text>}
          >
            {gradeRows.length > 0
              ? <ReactECharts option={gradeOption} style={{ height: 232 }} notMerge lazyUpdate />
              : <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无在籍学生" style={{ padding: '40px 0' }} />}
          </Card>
        </Col>
        <Col xs={24} lg={10}>
          <Card title="学院概览（在籍学生）" size="small" style={{ height: '100%' }}>
            {topBranches.length > 0
              ? <ReactECharts option={branchOption} style={{ height: 232 }} notMerge lazyUpdate />
              : <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无学院数据" style={{ padding: '40px 0' }} />}
          </Card>
        </Col>
      </Row>

      {/* ④ 教学运行：作业 / 课题 / 考勤（作业与课题尚未录入时显示占位，接口结构已留好） */}
      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col xs={24} md={8}>
          <Card
            title="作业完成情况"
            size="small"
            style={{ height: '100%' }}
            extra={hw.pending_grading > 0 ? <Tag color="red">{hw.pending_grading} 份待批</Tag> : null}
          >
            {hw.total === 0 ? (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={<Text type="secondary">暂无作业数据<br />教师布置作业后自动统计</Text>}
                style={{ padding: '28px 0' }}
              />
            ) : (
              <>
                <Statistic title="作业份数" value={hw.total} styles={{ content: { fontSize: 24 } }} />
                <div style={{ marginTop: 12 }}>
                  <Text type="secondary" style={{ fontSize: 12 }}>提交率（已提交 {hw.submissions} / 应缴 {hw.expected} 人次）</Text>
                  <Progress percent={hw.submission_rate} size="small" strokeColor="#1677ff" />
                </div>
                <Text type="secondary" style={{ fontSize: 12 }}>已批阅 {hw.graded} 份</Text>
              </>
            )}
          </Card>
        </Col>
        <Col xs={24} md={8}>
          <Card
            title="课题研究进度"
            size="small"
            style={{ height: '100%' }}
            extra={research.pending_review > 0 ? <Tag color="red">{research.pending_review} 份待审</Tag> : null}
          >
            {research.total === 0 ? (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={<Text type="secondary">暂无课题数据<br />发布课题后自动统计</Text>}
                style={{ padding: '28px 0' }}
              />
            ) : (
              <>
                <Statistic title="课题总数" value={research.total} styles={{ content: { fontSize: 24 } }} />
                <Row gutter={8} style={{ marginTop: 12 }}>
                  <Col span={12}><Statistic title="进行中" value={research.ongoing} styles={{ content: { fontSize: 18, color: '#1677ff' } }} /></Col>
                  <Col span={12}><Statistic title="已结题" value={research.finished} styles={{ content: { fontSize: 18, color: '#52c41a' } }} /></Col>
                </Row>
              </>
            )}
          </Card>
        </Col>
        <Col xs={24} md={8}>
          <Card
            title="考勤构成"
            size="small"
            style={{ height: '100%' }}
            extra={att.total > 0
              ? <Tooltip title={`已录考勤 ${att.sessions_recorded} / ${att.sessions_total} 个课次`}>
                <Tag color="blue">到课率 {att.attendance_rate}%</Tag>
              </Tooltip>
              : null}
          >
            {att.total === 0 ? (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={<Text type="secondary">暂无考勤记录<br />教师录入考勤后自动统计</Text>}
                style={{ padding: '28px 0' }}
              />
            ) : (
              <ReactECharts option={attendanceOption} style={{ height: 200 }} notMerge lazyUpdate />
            )}
          </Card>
        </Col>
      </Row>

      {/* ④ 最近日志 + 角色分布 / 快捷入口 */}
      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col xs={24} lg={14}>
          <Card
            title="最近操作日志"
            size="small"
            style={{ height: '100%' }}
            extra={<Button type="link" size="small" style={{ padding: 0 }} onClick={openLogs}>查看所有</Button>}
          >
            <List
              dataSource={recent_logs?.slice(0, 10) || []}
              locale={{ emptyText: '暂无操作日志' }}
              renderItem={(item: OperationLogItem) => {
                const desc = item.description?.startsWith(item.operator)
                  ? item.description.slice(item.operator.length)
                  : item.description;
                return (
                  <List.Item style={{ padding: '4px 0' }}>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {item.created_at ? `${item.created_at.slice(0, 4)}年${item.created_at.slice(5, 7)}月${item.created_at.slice(8, 10)}日 ${item.created_at.slice(11, 16)}` : '-'}
                      {' '}{item.operator}{desc}
                    </Text>
                  </List.Item>
                );
              }}
            />
          </Card>
        </Col>
        <Col xs={24} lg={10}>
          <Card title="角色分布" size="small" style={{ height: '100%' }}>
            <Row gutter={8}>
              {role_distribution?.map((r) => (
                <Col key={r.role} span={12} style={{ marginBottom: 8 }}>
                  <Tag color="blue">{roleDisplayMap[r.role] || r.role}: {r.count}人</Tag>
                </Col>
              ))}
            </Row>

            {open_enrollment_batches.length > 0 && (
              <>
                <Divider style={{ margin: '12px 0' }} />
                <Text type="secondary" style={{ fontSize: 12 }}>报名中批次</Text>
                <List
                  size="small"
                  dataSource={open_enrollment_batches}
                  renderItem={(batch) => (
                    <List.Item style={{ padding: '6px 0' }}>
                      <Text ellipsis style={{ maxWidth: 190 }}>{batch.title}</Text>
                      <Tag color={batch.days_left !== null && batch.days_left <= 3 ? 'orange' : 'blue'}>
                        {batch.days_left === null ? '未设截止' : batch.days_left >= 0 ? `剩 ${batch.days_left} 天` : '已过期'}
                      </Tag>
                    </List.Item>
                  )}
                />
              </>
            )}

            <Divider style={{ margin: '12px 0' }} />
            <Text type="secondary" style={{ fontSize: 12 }}>快捷入口</Text>
            <Space wrap style={{ marginTop: 8 }}>
              <Button size="small" icon={<UserOutlined />} onClick={() => navigate('/students')}>学生</Button>
              <Button size="small" icon={<BookOutlined />} onClick={() => navigate('/courses')}>课程</Button>
              <Button size="small" icon={<CalendarOutlined />} onClick={() => navigate('/activities')}>活动</Button>
              <Button size="small" icon={<TrophyOutlined />} onClick={() => navigate('/competitions')}>比赛</Button>
            </Space>
          </Card>
        </Col>
      </Row>

      <Modal
        title="操作日志"
        open={logsOpen}
        onCancel={() => setLogsOpen(false)}
        footer={null}
        width={960}
        destroyOnHidden
      >
        <Table
          rowKey="id"
          size="small"
          columns={logColumns}
          dataSource={logs}
          loading={logsLoading}
          scroll={{ y: 460 }}
          pagination={{
            current: logsPage,
            pageSize: 20,
            total: logsTotal,
            showSizeChanger: false,
            showTotal: (t) => `共 ${t} 条`,
            onChange: (page) => { setLogsPage(page); fetchLogs(page); },
          }}
        />
      </Modal>
    </>
  );
};

export default OverviewDashboard;
